import type { FloorPlannerLayout } from '@repo/frontend-types';
import {
  asArray,
  entityIdentityFor,
  isLive,
  layoutCurrentDesigns,
  registerRowName,
  surfaceName,
} from '@repo/front-end-utils';

import { indexPlanByIdentity, stripOrdinalSuffix } from './matchObserversToPlan';

/**
 * How the Observers notices name an entity they only hold a code for
 * (`onboarding-polish-observer-notices`, R1–R2).
 *
 * The notices used to print the code alone — `UGG-FIN-E209, UGG-FIN-E208 … name entities this plan
 * does not have` — and on staging Finsbury Park every observer and every composite is NAMED by its
 * code too (147 of 147 observers, 15 of 15 composites, measured 2026-10-06), so the stored names
 * add nothing. The names exist elsewhere: on the plan, and in the twin that is live now.
 *
 * **Name first, code in brackets, for everyone** (Rob, 2026-10-05). The code alone when no name is
 * found, or when the name says nothing the code does not.
 */
export const entityCodeLabel = (code: string, name: string | null | undefined): string => {
  const trimmed = name?.trim() ?? '';
  if (!trimmed || isSameCode(trimmed, code)) return code;
  return `${trimmed} (${code})`;
};

/** A code, case-folded and without an observer's `.N`: `ugg-fin-e209.1` → `UGG-FIN-E209`. */
const codeBase = (value: string): string => stripOrdinalSuffix(value.trim().toUpperCase());

/**
 * Whether a "name" is only another spelling of the code — the same identity with or without an
 * observer's `.N`, or the short form of it (`E209` for `UGG-FIN-E209`). Such a name says nothing the
 * code does not, so the code stands alone (Copilot review, PR #1933): an observer stored as
 * `UGG-FIN-E209.1` and named `UGG-FIN-E209` is not "UGG-FIN-E209 (UGG-FIN-E209.1)".
 */
const isSameCode = (name: string, code: string): boolean => {
  const a = codeBase(name);
  const b = codeBase(code);
  return a === b || b.endsWith(`-${a}`) || a.endsWith(`-${b}`);
};

/** A code (and, for an observer, its stored name) to the text a notice shows. */
export type EntityNamer = (code: string, storedName?: string | null) => string;

export interface EntityNamerSources {
  /** The session's plan — the names the operator set, so it is asked first. */
  plan: FloorPlannerLayout | null | undefined;
  /**
   * The twin's asset register, asked second (Rob, 2026-10-06). It names equipment the plan does not
   * hold: on staging Finsbury Park, 11 of the 14 observers whose entity is off the plan (`UGG-FIN-E209`
   * is "Water Tap"). Typed structurally, so any register row shape will do.
   */
  register?: ReadonlyArray<{
    entity_code?: string | null;
    name: string;
    asset_name?: string | null;
  }>;
  /** The twin that is live now, read back into a plan. `null` while loading, `none` or failed. */
  model: FloorPlannerLayout | null | undefined;
  /** The wizard's `?twinBid=`: the prefix every code is folded against. */
  twinBid: string | undefined;
}

/** One source's code → name lookup, folded by exactly the rule observers are placed by. */
const nameLookup = (
  layout: FloorPlannerLayout | null | undefined,
  twinBid: string | undefined,
): ((code: string) => string | null) => {
  if (!layout) return () => null;
  const index = indexPlanByIdentity(layout, twinBid);
  /*
   * The name AS SET, not a display name: `entityNamesByRefid` falls back to the generic word
   * (`Zone`, `Equipment`), and "Zone (UGG-FIN-Z022)" claims a name nobody gave it. An unnamed
   * entity is the code alone (R1). Same walk as the index — every floor's current design.
   */
  const names = new Map<string, string>();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const s of asArray(design?.surfaces).filter(isLive)) names.set(s.refid, surfaceName(s));
    for (const i of asArray(design?.items).filter(isLive)) names.set(i.refid, i.name?.trim() ?? '');
  }
  return code => {
    const folded = entityIdentityFor(twinBid, code);
    if (!folded) return null;
    const hit = index.get(folded) ?? index.get(stripOrdinalSuffix(folded));
    return hit ? names.get(hit.refid) || null : null;
  };
};

/**
 * Build the namer once per plan or model change — never per notice rendered.
 *
 * Sources in order (R2, amended by Rob 2026-10-06): the plan, then the asset register, then the
 * running model, then the observer's own stored name. A
 * code shown alone is NOT a claim that the entity has no name: the running-model read may have
 * failed, and the stage says so in its own banner beside it.
 *
 * ⚠ The code SHOWN is the code given. An observer's stored identifier keeps its `.N` — the operator
 * reads what the database holds (`StatementObserver.storedEntityBid`) — and only the lookup folds it.
 */
export const buildEntityNamer = ({
  plan,
  register,
  model,
  twinBid,
}: EntityNamerSources): EntityNamer => {
  const fromPlan = nameLookup(plan, twinBid);
  const fromRegister = registerLookup(register, twinBid);
  const fromModel = nameLookup(model, twinBid);
  return (code, storedName) =>
    entityCodeLabel(
      code,
      fromPlan(code) ?? fromRegister(code) ?? fromModel(code) ?? storedName ?? null,
    );
};

/** The register's code → name lookup: each row's `registerRowName`, under its folded entity code. */
const registerLookup = (
  rows: EntityNamerSources['register'],
  twinBid: string | undefined,
): ((code: string) => string | null) => {
  if (!rows?.length) return () => null;
  const names = new Map<string, string>();
  for (const row of rows) {
    const identity = row.entity_code ? entityIdentityFor(twinBid, row.entity_code) : '';
    const name = registerRowName(row)?.trim();
    // First named row wins, as the plan index's first writer does.
    if (identity && name && !names.has(identity)) names.set(identity, name);
  }
  return code => {
    const folded = entityIdentityFor(twinBid, code);
    if (!folded) return null;
    return names.get(folded) ?? names.get(stripOrdinalSuffix(folded)) ?? null;
  };
};
